<template>
    <b-row class="attendance-section employee-balance-card">
        <b-col cols="12" lg="3" class="mb-3">
            <h5 class="attendance-section-title text-primary">{{ type.label }}</h5>
            <div class="text-muted">{{ trans.subtitle }}</div>
        </b-col>

        <b-col cols="12" lg="9">
            <div class="balance-new form-row">
                <div class="form-group col-12 col-sm-6 mb-sm-0" :class="{'has-danger': !!fieldError('balance_date')}">
                    <label :for="'balance_date_' + type.key">{{ trans.balance_date }}</label>
                    <div class="input-group attendance-input-unit">
                        <datetime :id="'balance_date_' + type.key" v-model="balanceDate"
                                  :config="datePickerConfig" placeholder="dd.mm.yyyy" class="flatpickr"
                                  :class="{'form-control-danger': !!fieldError('balance_date')}"
                        ></datetime>
                        <div class="input-group-append">
                            <span class="input-group-text"><i class="fal fa-fw fa-calendar"></i></span>
                        </div>
                    </div>
                    <div v-if="fieldError('balance_date')" class="form-control-feedback form-text">{{ fieldError('balance_date') }}</div>
                </div>

                <div class="form-group col-12 col-sm-6 mb-0" :class="{'has-danger': !!fieldError('value')}">
                    <label :for="'balance_value_' + type.key">{{ type.label }}</label>
                    <div class="input-group attendance-input-unit">
                        <input type="text" :id="'balance_value_' + type.key" v-model="value" placeholder="0"
                               class="form-control text-right" :class="{'form-control-danger': !!fieldError('value')}">
                        <div class="input-group-append">
                            <span class="input-group-text">{{ trans.units[unit] }}</span>
                        </div>
                    </div>
                    <div v-if="fieldError('value')" class="form-control-feedback form-text">{{ fieldError('value') }}</div>
                </div>
            </div>

            <div class="balance-history">
                <table class="table mb-0">
                    <thead>
                    <tr>
                        <th>{{ trans.columns.balance_date }}</th>
                        <th class="text-right">{{ trans.columns.value }}</th>
                        <th class="w-100">{{ trans.columns.updated_at }}</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in history" :key="row.id" :class="{'text-muted': isDeleted(row)}">
                        <td class="text-nowrap">
                            <span :class="{'balance-deleted': isDeleted(row)}">{{ row.balance_date }}</span>
                            <span v-if="row.is_valid && !isDeleted(row)" class="badge balance-badge-valid ml-1">{{ trans.valid }}</span>
                        </td>
                        <td class="text-right text-nowrap" :class="{'balance-deleted': isDeleted(row)}">{{ formatValue(row.value) }}</td>
                        <td>
                            <template v-if="isDeleted(row)">{{ trans.deleted_on_save }}</template>
                            <template v-else>
                                {{ row.updated_at }}
                                <small class="text-muted ml-1" v-if="row.is_imported || row.updated_by">
                                    {{ row.is_imported ? trans.bulk_import : row.updated_by }}
                                </small>
                            </template>
                        </td>
                        <td class="text-right">
                            <button type="button" class="btn btn-link btn-sm p-0" :class="isDeleted(row) ? 'text-muted' : 'text-danger'"
                                    :title="isDeleted(row) ? trans.undo_delete : trans.delete" @click="toggleDeleted(row)">
                                <i class="fa fa-fw" :class="isDeleted(row) ? 'fa-undo' : 'fa-trash-alt'"></i>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="history.length === 0">
                        <td colspan="4" class="text-center text-muted py-3">{{ trans.no_history }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </b-col>
    </b-row>
</template>

<script>
import {formatNumberDynamic, normalizeFormValue} from "../utils";

// part of the employee form, the balance and the deleted history rows are saved with the form
export default {
    props: {
        'type': {
            type: Object
        },
        // days or hours, the balances are imported in days in SK and in hours otherwise
        'unit': {
            type: String
        },
        'history': {
            type: Array
        },
        'trans': {
            type: Object
        }
    },

    data: function () {
        return {
            balanceDate: null,
            value: '',
            deletedIds: [],
            // server errors by field, shown until the submitted value is changed
            serverErrors: {},
            datePickerConfig: {
                dateFormat: "Y-m-d",
                altInput: true,
                altFormat: "d.m.Y",
                locale: document.documentElement.lang,
                allowInput: true
            }
        };
    },

    methods: {
        formatValue(value) {
            return formatNumberDynamic(value, 0, 2) + ' ' + this.trans.units[this.unit];
        },

        isDeleted(row) {
            return this.deletedIds.includes(row.id);
        },

        toggleDeleted(row) {
            this.deletedIds = this.isDeleted(row)
                ? this.deletedIds.filter(id => id !== row.id)
                : this.deletedIds.concat([row.id]);
        },

        getData() {
            const value = ("" + this.value).trim().replace(",", ".");

            return {
                type: this.type.key,
                balance_date: this.balanceDate || null,
                value: value === '' ? null : value,
                deleted_ids: this.deletedIds
            };
        },

        // errors: [{field: 'balance_date' | 'value', msg: '...'}]
        setServerErrors(errors) {
            const values = {balance_date: this.balanceDate, value: this.value};

            this.serverErrors = {};
            errors.forEach(error => {
                this.$set(this.serverErrors, error.field, {msg: error.msg, value: normalizeFormValue(values[error.field])});
            });
        },

        fieldError(field) {
            const error = this.serverErrors[field];
            const value = field === 'balance_date' ? this.balanceDate : this.value;

            return error && error.value === normalizeFormValue(value) ? error.msg : null;
        }
    }
}
</script>

<style scoped>
.balance-new {
    border: 1px dashed #d8dde2;
    padding: 1.5rem;
    margin: 0 0 1.5rem 0;
}

.balance-history {
    border: 1px solid #e4e7ea;
}

/* the box has its own border, the global table header row borders would double it */
.balance-history thead tr {
    border: 0;
}

/* a single line under the header: no bootstrap 2px header border, no top border of the first row */
.balance-history th {
    /* the same grey as the input group addons */
    background-color: #e9ecef;
    border-top: 0;
    border-bottom: 1px solid #e4e7ea;
    color: #73818f;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
}

.balance-history td,
.balance-history th {
    padding: 0.4rem 1rem;
    vertical-align: middle;
}

.balance-history td {
    border-top-color: #eef0f2;
}

.balance-history tbody tr:first-child td {
    border-top: 0;
}

.balance-badge-valid {
    background-color: #e6f4ea;
    color: #1e7e34;
}

.balance-deleted {
    text-decoration: line-through;
}
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/employee/EmployeeBalanceCard.vue